import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; // Adjust path if needed
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { allPermissions } from '../config/permissions'; // Make sure this path is correct
import ProtectedComponent from '../components/ProtectedComponent'; // Adjust path if needed
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import './ListPage.css'; // Using a generic list page style

// --- The Modal Component ---
// A reusable, styled modal that matches your image.
const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <h2 style={styles.modalTitle}>{title}</h2>
                {children}
            </div>
        </div>
    );
};


const RolesPage = () => {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // State for the modal form
    const [editingRole, setEditingRole] = useState(null); // null for create, object for edit
    const [roleName, setRoleName] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState([]);

    // Fetch roles when the component mounts
    const fetchRoles = async () => {
        setIsLoading(true);
        try {
            const rolesCollection = collection(db, 'roles');
            const rolesSnapshot = await getDocs(rolesCollection);
            const rolesList = rolesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRoles(rolesList);
        } catch (error) {
            console.error("Error fetching roles:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    // --- Modal and Form Handlers ---

    const handleOpenCreateModal = () => {
        setEditingRole(null);
        setRoleName('');
        setSelectedPermissions([]);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (role) => {
        setEditingRole(role);
        setRoleName(role.id);
        setSelectedPermissions(role.permissions || []);
        setIsModalOpen(true);
    };
    
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handlePermissionCheckbox = (permission) => {
        setSelectedPermissions(prev => 
            prev.includes(permission)
                ? prev.filter(p => p !== permission)
                : [...prev, permission]
        );
    };

    // --- NEW: "Select All" Functionality ---
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedPermissions(allPermissions);
        } else {
            setSelectedPermissions([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!roleName.trim()) {
            alert('Please enter a role name.');
            return;
        }

        setIsLoading(true);

        try {
            if (editingRole) {
                // UPDATE existing role's permissions
                const roleRef = doc(db, 'roles', editingRole.id);
                await updateDoc(roleRef, { permissions: selectedPermissions });
                alert('Role updated successfully!');
            } else {
                // CREATE new role with the role name as the document ID
                const roleRef = doc(db, 'roles', roleName.trim());
                await setDoc(roleRef, { permissions: selectedPermissions });
                alert('Role created successfully!');
            }
            handleCloseModal();
            fetchRoles(); // Refresh the list
        } catch (error) {
            console.error("Error saving role:", error);
            alert('Failed to save role.');
            setIsLoading(false);
        }
    };

    const handleDelete = async (roleId) => {
        if (window.confirm(`Are you sure you want to delete the role "${roleId}"? This can affect users assigned to this role.`)) {
            try {
                await deleteDoc(doc(db, 'roles', roleId));
                setRoles(roles.filter(role => role.id !== roleId));
                alert('Role deleted successfully!');
            } catch (error) {
                console.error("Error deleting role:", error);
                alert('Failed to delete role.');
            }
        }
    };

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1>Role Management</h1>
                <ProtectedComponent requiredPermission="roles.create">
                    <button className="add-button" onClick={handleOpenCreateModal}>
                        <FaPlus style={{ marginRight: '8px' }} />
                        Create New Role
                    </button>
                </ProtectedComponent>
            </div>

            {isLoading ? <p>Loading roles...</p> : (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Role Name</th>
                                <th>Permissions Count</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map(role => (
                                <tr key={role.id}>
                                    <td>{role.id}</td>
                                    <td>{role.permissions ? role.permissions.length : 0}</td>
                                    <td className="actions-cell">
                                        <ProtectedComponent requiredPermission="roles.update">
                                            <button className="action-button edit" onClick={() => handleOpenEditModal(role)}><FaEdit /></button>
                                        </ProtectedComponent>
                                        <ProtectedComponent requiredPermission="roles.delete">
                                            <button className="action-button delete" onClick={() => handleDelete(role.id)}><FaTrash /></button>
                                        </ProtectedComponent>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- The Modal for Creating/Editing Roles --- */}
            <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingRole ? `Edit Role: ${editingRole.id}` : 'Create New Role'}>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <input
                            type="text"
                            placeholder="Role Name (e.g., Accountant)"
                            value={roleName}
                            onChange={(e) => setRoleName(e.target.value)}
                            disabled={!!editingRole} // Disable changing the name when editing
                            style={styles.input}
                            required
                        />
                    </div>
                    
                    <h4 style={styles.permissionsTitle}>Assign Permissions</h4>
                    
                    <div style={styles.permissionsContainer}>
                         {/* --- "Select All" Checkbox --- */}
                        <div style={styles.permissionItem}>
                            <label style={{ fontWeight: 'bold' }}>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={selectedPermissions.length === allPermissions.length}
                                    // Shows a dash if some but not all are selected
                                    ref={el => el && (el.indeterminate = selectedPermissions.length > 0 && selectedPermissions.length < allPermissions.length)}
                                />
                                Select All
                            </label>
                        </div>
                        <hr style={styles.divider} />
                        {allPermissions.map(permission => (
                            <div key={permission} style={styles.permissionItem}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={selectedPermissions.includes(permission)}
                                        onChange={() => handlePermissionCheckbox(permission)}
                                    />
                                    {permission}
                                </label>
                            </div>
                        ))}
                    </div>
                    <div style={styles.modalActions}>
                        <button type="submit" style={styles.actionButton} disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save Role'}
                        </button>
                         <button type="button" onClick={handleCloseModal} style={styles.actionButton}>Close</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

// --- STYLES to match your image ---
const styles = {
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    },
    modalContent: {
        background: 'white', padding: '25px', borderRadius: '8px',
        width: '90%', maxWidth: '400px',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    },
    modalTitle: {
        textAlign: 'center', margin: '0 0 20px 0', fontSize: '1.5rem',
        fontWeight: '600', color: '#333',
    },
    formGroup: {
        marginBottom: '20px',
    },
    input: {
        width: '100%', padding: '10px', fontSize: '1rem',
        border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box',
    },
    permissionsTitle: {
        margin: '0 0 10px 0', fontSize: '1.1rem', fontWeight: 'bold',
    },
    permissionsContainer: {
        maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd',
        borderRadius: '4px', padding: '10px', marginBottom: '20px',
    },
    permissionItem: {
        padding: '4px 0',
    },
    divider: {
        border: 'none',
        borderTop: '1px solid #eee',
        margin: '5px 0'
    },
    modalActions: {
        display: 'flex', justifyContent: 'flex-start',
        gap: '10px', marginTop: '10px',
    },
    actionButton: {
        padding: '8px 16px', border: '1px solid #999',
        background: '#f0f0f0', cursor: 'pointer', borderRadius: '4px',
    },
};

export default RolesPage;